import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { AbstractSearchCore, AbstractSubscriber, ISearchConfig } from '../../modules/search'
Locale.getInstance().registerKeySet('fr', 'similarEvents', {
  seeMore: {
    eventOffers: "Voir plus d'événements similaires",
    exhibitionEvents: "Voir plus d'expositions similaires",
    eventSeries: 'Voir plus de séries similaires',
  },
})

Locale.getInstance().registerKeySet('en', 'similarEvents', {
  seeMore: {
    eventOffers: 'See more similar events',
    exhibitionsEvents: 'See more similar exhibitions',
    eventSeries: 'See more similar series',
  },
})
class LvcSimilarEventsSubscriber extends AbstractSubscriber {
  core: AbstractSearchCore | null = null
  readyCallback: () => void

  constructor(groupId: string, onReadyCallback: () => void) {
    super(groupId)
    this.readyCallback = onReadyCallback
  }

  async onFacetUpdate(): Promise<void> {}

  async onResultUpdate(): Promise<void> {}

  async attached(core: AbstractSearchCore): Promise<void> {
    this.core = core
    this.readyCallback?.()
  }

  detaching(): void {}

  async onSearchConfigUpdate(config: any): Promise<void> {}

  async onSearchTextUpdate(): Promise<void> {}

  public async fetchItems(schema: string, disciplines: string[] = []): Promise<any[]> {
    if (!this.core) return []

    const disciplineFacet = {
      name: 'uiAdditionalTypesCode',
      options: disciplines.map(discipline => ({
        value: discipline,
        selected: true,
        available: true,
      })),
    }
    let index
    switch (schema) {
      case 'eventSeries':
        index = 'eventseries_search'
        break
      case 'exhibitionEvents':
        index = 'exhibitions_search'
        break
      default:
        index = 'events_search'
    }
    const searchConfig: ISearchConfig = {
      facets: [disciplineFacet],
      index: [index],
      modal: index,
    }

    const response = await this.core.executeSearch(searchConfig)
    return Object.values(response).flatMap((res: any) => res?.items ?? [])
  }
}

export interface LvcSimilarEvents {
  resultsLimit: number
  disciplines: string
  dataSchemaType?: string | null
  dataStartDate?: string | null
  dataEndDate?: string | null
  dataObjectId?: string | null
}

export class LvcSimilarEvents extends MinzeElement {
  attrs: Attrs = [
    'disciplines',
    'data-schema-type',
    'data-start-date',
    'data-end-date',
    'data-object-id',
    'data-event-id',
  ]
  reactive: Reactive = [['displaySearchButton', false]]

  subscriber: LvcSimilarEventsSubscriber | null = null
  resultsLimit = 15
  items: any[] = []

  readyCallback = async () => {
    await MainModule.getInstance().getGeo().whenReady()

    const parseInput = (input: string | undefined) => {
      if (!input) return []
      try {
        return JSON.parse(decodeURIComponent(input))
      } catch {
        return decodeURIComponent(input).split(',') ?? []
      }
    }

    const disciplines = parseInput(this.disciplines as string)
    const schemas = parseInput(this.dataSchemaType as string)

    // Fetch items with or without disciplines

    // First try with disciplines
    let response =
      (await this.subscriber?.fetchItems(this.dataSchemaType as string, disciplines as string[])) ??
      []
    // If no results, try without disciplines
    if (response.length === 0) {
      response = (await this.subscriber?.fetchItems(this.dataSchemaType as string, [])) ?? []
    }

    // Filter out the current dataObjectId and dataEventId
    if (this.dataObjectId) {
      response = response.filter((item: any) => item.objectID !== this.dataObjectId)
    }
    // Filter out the current dataEventId
    if (this.dataEventId) {
      const parsedEventId = parseInt(this.dataEventId as string)
      response = response.filter((item: any) => item.eventId !== parsedEventId)
    }

    // Update items and rerender
    this.items.splice(0, this.items.length)
    if (response.length > this.resultsLimit) {
      this.items.push(...response.splice(0, this.resultsLimit))
      this.displaySearchButton = true
    } else {
      this.items.push(...response)
    }

    this.rerender()
  }

  onReady = () => {
    this.subscriber = new LvcSimilarEventsSubscriber('similarEvents', this.readyCallback)
    MainModule.getInstance().getSearchManager().subscribe(this.subscriber)
  }

  onSeeMoreClick = () => {
    this.subscriber?.core?.goToSearch()
  }

  html = () => `
    <lvc-search-result-block
      results="${encodeURIComponent(JSON.stringify(this.items))}"
      data-modal="${this.subscriber?.core?.searchConfig?.modal}"
      enable-date-blocks="false"
      data-component-location="evenements_similaires"
    >
    </lvc-search-result-block>
    ${
      this.displaySearchButton
        ? /*html*/ `
      <lvc-box display="flex" justifyContent="center" mt="4rem" padding="0 0 2rem 0">
        <lvc-button enable-hover-box-shadow="true" bg="white" border="1px solid #1E007D" color="#1E007D">${Locale.getKey(`similarEvents.seeMore.${this.dataSchemaType}`)}</lvc-button>
      <lvc-box>
    `
        : ''
    }
  `

  eventListeners: EventListeners = [['lvc-button', 'click', this.onSeeMoreClick]]
}
